/** Compact live-map toolbar for basemap, 3D terrain, and distance measuring. */

import { memo, useEffect, useId, useRef, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";

type Basemap = "street" | "satellite";

interface MapToolbarProps {
  effectiveBasemap: Basemap;
  is3D: boolean;
  mapOpacity: number;
  measurementActive: boolean;
  measurementPointCount: number;
  measurementTotalMeters: number;
  measurementLastSegmentMeters: number;
  onBasemapChange: (basemap: Basemap) => void;
  onMapOpacityChange: (opacity: number) => void;
  onToggle3D: () => void;
  onToggleMeasurement: () => void;
  onFinishMeasurement: () => void;
  onUndoMeasurement: () => void;
  onClearMeasurement: () => void;
}

function formatDistance(meters: number, lang: string): string {
  const locale = lang === "de" ? "de-DE" : "en";
  if (meters < 1000) {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: meters < 100 ? 1 : 0,
    }).format(meters)} m`;
  }
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: meters < 10000 ? 2 : 1,
    minimumFractionDigits: meters < 10000 ? 2 : 1,
  }).format(meters / 1000)} km`;
}

function toolButtonClass(active: boolean): string {
  return [
    "flex h-9 min-w-9 items-center justify-center gap-1 rounded-md px-1.5",
    "text-[0.68rem] font-semibold transition-colors focus-visible:outline-none",
    "sm:h-10 sm:min-w-10 sm:gap-1.5 sm:px-2 sm:text-xs",
    "focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none",
    "disabled:cursor-not-allowed disabled:opacity-50",
    active
      ? "bg-flima-spruce text-white"
      : "text-flima-slate/80 hover:bg-flima-mist hover:text-flima-spruce",
  ].join(" ");
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9 18 3 21V6l6-3 6 3 6-3v15l-6 3-6-3Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 3v15M15 6v15" strokeLinecap="round" />
    </svg>
  );
}

function SatelliteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="7" />
      <path d="M5 12h14M12 5a10 10 0 0 1 0 14M12 5a10 10 0 0 0 0 14" strokeLinecap="round" />
    </svg>
  );
}

function RulerIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="m4 17 13-13 3 3L7 20l-3-3Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m8 13 2 2M11 10l2 2M14 7l2 2" strokeLinecap="round" />
    </svg>
  );
}

function ToolsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 7h16M4 17h16" strokeLinecap="round" />
      <circle cx="9" cy="7" r="2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="17" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export const MapToolbar = memo(function MapToolbar({
  effectiveBasemap,
  is3D,
  mapOpacity,
  measurementActive,
  measurementPointCount,
  measurementTotalMeters,
  measurementLastSegmentMeters,
  onBasemapChange,
  onMapOpacityChange,
  onToggle3D,
  onToggleMeasurement,
  onFinishMeasurement,
  onUndoMeasurement,
  onClearMeasurement,
}: MapToolbarProps) {
  const { t, lang } = useLanguageContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const showMeasurementPanel = measurementActive || measurementPointCount > 0;
  const mapOpacityPercent = Math.round(mapOpacity * 100);
  const measurementToggleLabel = measurementActive
    ? t("measurement_pause")
    : measurementPointCount > 0
      ? t("measurement_resume")
      : t("measurement_start");

  useEffect(() => {
    if (!mobileOpen) return;
    const handlePointerOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setMobileOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handlePointerOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [mobileOpen]);

  return (
    <div
      ref={containerRef}
      data-tour-id="map-tools"
      className="pointer-events-none absolute left-12 top-2.5 z-10 flex max-w-[calc(100vw-4rem)] flex-col items-start gap-1.5 sm:top-3 sm:max-w-[calc(100%-5.75rem)] sm:gap-2"
    >
      <button
        type="button"
        onClick={() => setMobileOpen((previous) => !previous)}
        aria-expanded={mobileOpen}
        aria-controls={panelId}
        aria-label={t("map_tools")}
        title={t("map_tools")}
        className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-xl border border-white/70 bg-white/95 text-flima-slate shadow-float backdrop-blur transition-colors hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river sm:hidden motion-reduce:transition-none"
      >
        <ToolsIcon />
      </button>

      <div
        id={panelId}
        className={`${
          mobileOpen ? "flex" : "hidden"
        } pointer-events-auto w-[min(16rem,calc(100vw-6.5rem))] max-w-full flex-wrap items-center gap-0.5 rounded-lg border border-white/70 bg-white/95 p-0.5 shadow-float backdrop-blur sm:flex sm:w-auto sm:gap-1 sm:p-1`}
      >
        <div
          role="group"
          aria-label={t("basemap")}
          className="flex rounded-md border border-flima-spruce/10 bg-flima-mist/35 p-0.5"
        >
          <button
            type="button"
            onClick={() => onBasemapChange("street")}
            disabled={is3D}
            aria-pressed={effectiveBasemap === "street"}
            aria-label={t("basemap_street")}
            title={is3D ? t("basemap_locked_3d") : t("basemap_street")}
            className={toolButtonClass(effectiveBasemap === "street")}
          >
            <MapIcon />
            <span className="hidden sm:inline">{t("basemap_street")}</span>
          </button>
          <button
            type="button"
            onClick={() => onBasemapChange("satellite")}
            disabled={is3D}
            aria-pressed={effectiveBasemap === "satellite"}
            aria-label={t("basemap_satellite")}
            title={is3D ? t("basemap_locked_3d") : t("basemap_satellite")}
            className={toolButtonClass(effectiveBasemap === "satellite")}
          >
            <SatelliteIcon />
            <span className="hidden sm:inline">{t("basemap_satellite")}</span>
          </button>
        </div>

        <span className="h-7 w-px bg-flima-spruce/15 sm:h-8" aria-hidden="true" />

        <label className="flex h-9 min-w-[6.75rem] items-center gap-1 rounded-md px-1 text-[0.68rem] font-semibold text-flima-slate/70 sm:h-10 sm:min-w-[8.75rem] sm:gap-2 sm:px-2 sm:text-xs">
          <span className="hidden sm:inline">{t("map_opacity")}</span>
          <input
            type="range"
            min="0.2"
            max="1"
            step="0.05"
            value={mapOpacity}
            onChange={(event) => onMapOpacityChange(Number(event.currentTarget.value))}
            aria-label={t("map_opacity")}
            className="h-1 w-12 accent-flima-spruce sm:w-20"
          />
          <span className="w-7 text-right tabular-nums text-flima-slate/60 sm:w-8">
            {mapOpacityPercent}%
          </span>
        </label>

        <span className="h-7 w-px bg-flima-spruce/15 sm:h-8" aria-hidden="true" />

        <button
          type="button"
          onClick={onToggle3D}
          aria-pressed={is3D}
          aria-label={is3D ? t("map_2d") : t("map_3d")}
          title={is3D ? t("map_2d") : t("map_3d")}
          className={toolButtonClass(is3D)}
        >
          <span className="font-utility text-[0.7rem] tracking-[0.08em]">
            {is3D ? "2D" : "3D"}
          </span>
        </button>

        <button
          type="button"
          onClick={onToggleMeasurement}
          aria-pressed={measurementActive}
          aria-label={measurementToggleLabel}
          title={measurementToggleLabel}
          className={toolButtonClass(measurementActive)}
        >
          <RulerIcon />
        </button>
      </div>

      {showMeasurementPanel ? (
        <div className="pointer-events-auto w-[min(18rem,calc(100vw-6.5rem))] rounded-lg border border-white/70 bg-white/95 p-3 text-xs text-flima-slate shadow-float backdrop-blur sm:w-[min(18rem,calc(100vw-5.75rem))]">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-utility text-[0.68rem] uppercase tracking-[0.14em] text-flima-slate/60">
              {t("measurement_distance")}
            </h2>
            <span className="font-semibold tabular-nums text-flima-spruce" aria-live="polite">
              {formatDistance(measurementTotalMeters, lang)}
            </span>
          </div>

          <dl className="mt-2 grid grid-cols-3 gap-2">
            <div className="rounded-md bg-flima-mist/60 px-2 py-1.5">
              <dt className="text-[0.62rem] uppercase tracking-[0.08em] text-flima-slate/55">
                {t("measurement_points")}
              </dt>
              <dd className="mt-0.5 font-semibold tabular-nums">{measurementPointCount}</dd>
            </div>
            <div className="col-span-2 rounded-md bg-flima-mist/60 px-2 py-1.5">
              <dt className="text-[0.62rem] uppercase tracking-[0.08em] text-flima-slate/55">
                {t("measurement_last_segment")}
              </dt>
              <dd className="mt-0.5 font-semibold tabular-nums">
                {formatDistance(measurementLastSegmentMeters, lang)}
              </dd>
            </div>
          </dl>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onFinishMeasurement}
              disabled={!measurementActive || measurementPointCount < 2}
              className="min-h-9 rounded-md bg-flima-spruce px-3 text-xs font-semibold text-white transition-colors hover:bg-flima-spruce/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            >
              {t("measurement_finish")}
            </button>
            <button
              type="button"
              onClick={onUndoMeasurement}
              disabled={measurementPointCount === 0}
              className="min-h-9 rounded-md border border-flima-spruce/15 bg-white px-3 text-xs font-semibold text-flima-slate transition-colors hover:bg-flima-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            >
              {t("measurement_undo")}
            </button>
            <button
              type="button"
              onClick={onClearMeasurement}
              disabled={measurementPointCount === 0}
              className="min-h-9 rounded-md border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            >
              {t("measurement_clear")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
});
